'use client';

import * as React from 'react';
import { Box, Menu, MenuItem } from '@mui/material';
import {
  ChartContainer,
  BarPlot,
  LinePlot,
  MarkPlot,
  ChartsXAxis,
  ChartsYAxis,
  ChartsGrid,
  ChartsTooltip,
  ChartsLegend,
} from '@mui/x-charts';
import { MarkElement } from '@mui/x-charts/LineChart';
import type { MarkElementProps } from '@mui/x-charts/LineChart';
import { LineChartWrapper, BarChartWrapper } from '../../charts';
import { formatNumberItalian as formatNumberItalianGrouped } from '../../../utils/numberFormat';

/** Mark con fill pieno (stesso colore della serie) per asse X non periodo */
function FilledMarkElement(props: MarkElementProps) {
  const { color, ...rest } = props;
  const fillColor = color ?? 'currentColor';
  return (
    <MarkElement
      {...rest}
      color={color}
      fill={fillColor}
      stroke={fillColor}
      strokeWidth={1}
      sx={{ fill: fillColor, stroke: fillColor, strokeWidth: 1 }}
    />
  );
}

const MONTH_LABELS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

const MONTH_SHORT: Record<number, string> = {
  0: 'gen', 1: 'feb', 2: 'mar', 3: 'apr', 4: 'mag', 5: 'giu', 6: 'lug', 7: 'ago', 8: 'set', 9: 'ott', 10: 'nov', 11: 'dic',
};

function getMonth(date: Date): string {
  return MONTH_SHORT[date.getMonth()] ?? date.toLocaleDateString('it-IT', { month: 'short' });
}
function getYear(date: Date): string {
  return date.getFullYear().toString();
}
function formatMonthYear(date: Date): string {
  return `${getMonth(date)} ${getYear(date)}`;
}

/** Formatta numeri in stile italiano: 1.222.555,12 (migliaia con punto, decimali con virgola). */
function formatNumberItalian(value: number, maxDecimals = 2): string {
  if (!Number.isFinite(value)) return String(value);
  return formatNumberItalianGrouped(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  });
}

/** Formatta slope/intercept con abbastanza decimali da non nascondere valori piccoli (es. 0.003 non "0.00"). */
function formatCoefficient(n: number): string {
  const a = Math.abs(n);
  if (a >= 0.01) return formatNumberItalian(n, 2);
  if (a >= 1e-6) return formatNumberItalian(n, 6);
  return n.toExponential(2);
}

export interface ChartSeriesConfig {
  data: (number | null)[];
  label: string;
  color: string;
  yAxisId?: string;
  id?: string;
}

export interface CorrelationResult {
  slope: number;
  intercept: number;
  r2: number;
}

export type ConsumptionCostsChartViewProps =
  | {
      kind: 'barMonth';
      seriesByYear: { data: number[]; label: string }[];
      width?: number;
      height?: number;
    }
  | {
      kind: 'bar' | 'line' | 'points';
      xAxisData: (string | number | Date)[];
      xScaleType: 'point' | 'linear';
      xAxisLabel?: string;
      yAxisLabel?: string;
      xAxisMonthStyle?: boolean;
      series: ChartSeriesConfig[];
      correlation?: CorrelationResult;
      showOnlyPoints?: boolean;
      y1AsBars?: boolean;
      width?: number;
      height?: number;
    };

function linearRegression(
  xValues: number[],
  yValues: number[]
): CorrelationResult | undefined {
  const n = xValues.length;
  if (n < 2) return undefined;
  const valid: { x: number; y: number }[] = [];
  for (let i = 0; i < n; i++) {
    const x = xValues[i];
    const y = yValues[i];
    if (typeof x === 'number' && !Number.isNaN(x) && typeof y === 'number' && !Number.isNaN(y)) {
      valid.push({ x, y });
    }
  }
  if (valid.length < 2) return undefined;
  const sumX = valid.reduce((s, p) => s + p.x, 0);
  const sumY = valid.reduce((s, p) => s + p.y, 0);
  const sumXY = valid.reduce((s, p) => s + p.x * p.y, 0);
  const sumX2 = valid.reduce((s, p) => s + p.x * p.x, 0);
  const nn = valid.length;
  const denom = nn * sumX2 - sumX * sumX;
  if (Math.abs(denom) < 1e-12) return undefined;
  const slope = (nn * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / nn;
  const meanY = sumY / nn;
  let ssTot = 0;
  let ssRes = 0;
  valid.forEach(p => {
    const pred = slope * p.x + intercept;
    ssTot += (p.y - meanY) ** 2;
    ssRes += (p.y - pred) ** 2;
  });
  const r2 = ssTot > 0 ? 1 - ssRes / ssTot : 0;
  return { slope, intercept, r2 };
}

function useContainerWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(0);
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [containerRef, width];
}

function useChartDownload(ref: React.RefObject<HTMLDivElement | null>) {
  const [menuPos, setMenuPos] = React.useState<{ mouseX: number; mouseY: number } | null>(null);
  const getSvg = React.useCallback((): SVGSVGElement | null => {
    const svg = ref.current?.querySelector('svg');
    if (!svg) return null;
    const svgClone = svg.cloneNode(true) as SVGSVGElement;
    let css = '';
    for (const sheet of Array.from(document.styleSheets)) {
      try {
        for (const rule of Array.from(sheet.cssRules)) css += rule.cssText;
      } catch {
        // cross-origin
      }
    }
    css += `
      .MuiChartsAxis-line,.MuiChartsAxis-tick,.MuiChartsAxis-label,.MuiChartsAxis-tickLabel { fill: #000 !important; }
      .MuiChartsLegend-root,.MuiChartsLegend-root *,.MuiChartsLegend-root text,.MuiChartsLegend-root tspan { color: #000 !important; }
      .MuiChartsLegend-root text { fill: inherit !important; }
    `;
    const styleEl = document.createElement('style');
    styleEl.textContent = css;
    svgClone.insertBefore(styleEl, svgClone.firstChild);
    return svgClone;
  }, [ref]);
  const handleClose = React.useCallback(() => setMenuPos(null), []);
  const handleExportSvg = React.useCallback(() => {
    const svgClone = getSvg();
    if (!svgClone) return;
    const blob = new Blob([new XMLSerializer().serializeToString(svgClone)], { type: 'image/svg+xml;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'grafico-misto.svg';
    link.click();
    URL.revokeObjectURL(link.href);
    handleClose();
  }, [getSvg, handleClose]);
  const handleExportPng = React.useCallback(() => {
    const svgClone = getSvg();
    if (!svgClone) return;
    const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(svgClone)], { type: 'image/svg+xml;charset=utf-8' }));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        return;
      }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(blob => {
        if (!blob) {
          URL.revokeObjectURL(url);
          return;
        }
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'grafico-misto.png';
        a.click();
        URL.revokeObjectURL(a.href);
        URL.revokeObjectURL(url);
        handleClose();
      }, 'image/png');
    };
    img.onerror = () => URL.revokeObjectURL(url);
    img.src = url;
  }, [getSvg, handleClose]);
  const handleContextMenu = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setMenuPos({ mouseX: e.clientX + 2, mouseY: e.clientY - 6 });
  }, []);
  return { menuPos, handleClose, handleExportSvg, handleExportPng, handleContextMenu };
}

export function ConsumptionCostsChartView(props: ConsumptionCostsChartViewProps) {
  const [containerRef, containerWidth] = useContainerWidth();
  const width = props.width ?? (containerWidth > 0 ? containerWidth : 600);
  const height = props.height ?? 400;
  const chartDownload = useChartDownload(containerRef);

  if (props.kind === 'barMonth') {
    return (
      <Box ref={containerRef} sx={{ width: '100%', overflow: 'auto' }}>
        <BarChartWrapper
          xAxis={[{ scaleType: 'band', data: MONTH_LABELS }]}
          yAxis={[{ scaleType: 'linear', min: 0, valueFormatter: (v: number) => formatNumberItalian(v) }]}
          series={props.seriesByYear.map(s => ({
            ...s,
            data: s.data.map(v => (Number.isNaN(v) ? null : v)),
          }))}
          width={Math.max(width, 400)}
          height={height}
          margin={{ top: 24, right: 60, left: 60, bottom: 80 }}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'bottom', horizontal: 'middle' },
            },
          }}
        />
      </Box>
    );
  }

  const {
    xAxisData,
    xScaleType,
    xAxisLabel,
    xAxisMonthStyle = false,
    series,
    correlation,
    showOnlyPoints = false,
    y1AsBars = false,
  } = props;

  const isXAxisDates = xAxisMonthStyle && xAxisData.length > 0 && xAxisData[0] instanceof Date;
  const bandAxisData = xAxisData.map(v => (v instanceof Date ? v : String(v ?? '')));
  const bandAxisExtras =
    xAxisMonthStyle
      ? {
          tickSize: 8,
          height: 100,
          valueFormatter: (v: string | Date) => (v instanceof Date ? formatMonthYear(v) : String(v ?? '')),
          tickLabelStyle: { angle: -45, textAnchor: 'end'} as any,
        }
      : {};
  const bottomMargin = xAxisMonthStyle ? 140 : 80;

  const hasTwoSeries = series.length === 2;
  const marginRight = hasTwoSeries ? 140 : 90;

  const REGRESSION_SERIES_ID = 'regression-line';

  // Regression line series (when correlation and numeric X)
  const correlationSeries: ChartSeriesConfig | null =
    correlation && xScaleType === 'linear' && Array.isArray(xAxisData) && xAxisData.length > 0 && series[0]
      ? (() => {
          const xNum = xAxisData as number[];
          const yLine = xNum.map(x => correlation.slope * x + correlation.intercept);
          return {
            id: REGRESSION_SERIES_ID,
            data: yLine,
            label: `Regressione: y = ${formatCoefficient(correlation.slope)}x + ${formatCoefficient(correlation.intercept)} R² = ${formatNumberItalian(correlation.r2, 4)}`,
            color: '#666',
            yAxisId: series[0].yAxisId ?? 'left',
          };
        })()
      : null;

  const allSeries = correlationSeries ? [...series, correlationSeries] : series;

  if (y1AsBars && series.length === 1) {
    return (
      <Box ref={containerRef} sx={{ width: '100%', overflow: 'auto' }}>
        <BarChartWrapper
          xAxis={[
            {
              scaleType: 'band',
              data: bandAxisData,
              valueFormatter: (v: string | number | Date) => (v instanceof Date ? formatMonthYear(v) : String(v ?? '')),
              ...(xAxisLabel ? { label: xAxisLabel } : {}),
              ...bandAxisExtras,
            },
          ]}
          yAxis={
            hasTwoSeries
              ? [
                  { id: 'left', scaleType: 'linear', position: 'left', min: 0, valueFormatter: (v: number) => formatNumberItalian(v) },
                  { id: 'right', scaleType: 'linear', position: 'right', min: 0, valueFormatter: (v: number) => formatNumberItalian(v) },
                ]
              : [{ id: 'left', scaleType: 'linear', min: 0, valueFormatter: (v: number) => formatNumberItalian(v) }]
          }
          series={allSeries.map(s => ({
            data: s.data.map(v => (v != null && !Number.isNaN(v) ? v : null)),
            label: s.label,
            color: s.color,
            ...(s.yAxisId ? { yAxisId: s.yAxisId } : {}),
          }))}
          width={Math.max(width, (Array.isArray(xAxisData) ? xAxisData.length : 0) * 32)}
          height={height}
          margin={{ top: 40, right: marginRight, left: 90, bottom: bottomMargin }}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'bottom', horizontal: 'middle' },
            },
          }}
        />
      </Box>
    );
  }

  if (y1AsBars && series.length === 2) {
    const s0 = series[0];
    const s1 = series[1];
    const dataset = xAxisData.map((x, i) => ({
      month: x instanceof Date ? x : String(x ?? ''),
      y1: s0.data[i] != null && !Number.isNaN(s0.data[i]) ? s0.data[i]! : null,
      y2: s1.data[i] != null && !Number.isNaN(s1.data[i]) ? s1.data[i]! : null,
    }));
    const mixedSeries = [
      { type: 'bar' as const, dataKey: 'y1', label: s0.label, color: s0.color, yAxisId: s0.yAxisId ?? 'left' },
      { type: 'line' as const, dataKey: 'y2', label: s1.label, color: s1.color, yAxisId: s1.yAxisId ?? 'right', showMark: !xAxisMonthStyle },
    ];

    return (
      <Box
        ref={containerRef}
        sx={{ width: '100%', overflow: 'auto' }}
        onContextMenu={chartDownload.handleContextMenu}
      >
        <ChartContainer
          series={mixedSeries}
          xAxis={[
            {
              scaleType: 'band',
              dataKey: 'month',
              valueFormatter: (v: string | Date) => (v instanceof Date ? formatMonthYear(v) : String(v ?? '')),
              ...(xAxisLabel ? { label: xAxisLabel } : {}),
              ...bandAxisExtras,
            },
          ]}
          yAxis={[
            { id: 'left', scaleType: 'linear', min: 0, valueFormatter: (v: number) => formatNumberItalian(v) },
            { id: 'right', scaleType: 'linear', position: 'right', min: 0, valueFormatter: (v: number) => formatNumberItalian(v) },
          ]}
          dataset={dataset}
          width={Math.max(width, dataset.length * 32)}
          height={height}
          margin={{ top: 40, right: 90, left: 90, bottom: bottomMargin }}
        >
          <ChartsGrid horizontal />
          <BarPlot />
          <LinePlot />
          <MarkPlot />
          <ChartsXAxis />
          <ChartsYAxis axisId="left" />
          <ChartsYAxis axisId="right" />
          <ChartsTooltip />
          <ChartsLegend direction="row" position={{ vertical: 'bottom', horizontal: 'middle' }} />
        </ChartContainer>
        <Menu
          open={chartDownload.menuPos !== null}
          onClose={chartDownload.handleClose}
          anchorReference="anchorPosition"
          anchorPosition={chartDownload.menuPos ? { top: chartDownload.menuPos.mouseY, left: chartDownload.menuPos.mouseX } : undefined}
        >
          <MenuItem onClick={chartDownload.handleExportSvg}>Scarica SVG</MenuItem>
          <MenuItem onClick={chartDownload.handleExportPng}>Scarica PNG</MenuItem>
        </Menu>
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{ width: '100%', overflow: 'auto' }}>
      <LineChartWrapper
        width={Math.max(width, (Array.isArray(xAxisData) ? xAxisData.length : 0) * 32)}
        height={height}
        series={allSeries.map(s => ({
          ...(s.id ? { id: s.id } : {}),
          data: s.data.map(v => (v != null && !Number.isNaN(v) ? v : null)),
          label: s.label,
          color: s.color,
          yAxisId: s.yAxisId,
          showMark: !xAxisMonthStyle && s.id !== REGRESSION_SERIES_ID,
          connectNulls: false,
        }))}
        slots={!xAxisMonthStyle ? { mark: FilledMarkElement } : undefined}
        slotProps={{
          legend: {
            direction: 'row',
            position: { vertical: 'bottom', horizontal: 'middle' },
          },
          ...(showOnlyPoints && correlationSeries
            ? {
                line: (ownerState: { id?: string | number }) =>
                  ownerState.id === REGRESSION_SERIES_ID ? {} : { style: { stroke: 'none' } },
              }
            : showOnlyPoints
              ? { line: { style: { stroke: 'none' } } }
              : {}),
        }}
        xAxis={[
          xScaleType === 'linear'
            ? {
                data: xAxisData as number[],
                scaleType: 'linear' as const,
                valueFormatter: (v: number) => formatNumberItalian(v),
                ...(xAxisLabel ? { label: xAxisLabel } : {}),
              }
            : {
                data: bandAxisData as string[],
                scaleType: 'point' as const,
                valueFormatter: (v: string | Date) => (v instanceof Date ? formatMonthYear(v) : String(v ?? '')),
                ...(xAxisLabel ? { label: xAxisLabel } : {}),
                ...(isXAxisDates ? { tickSize: 8, height: 100, tickLabelStyle: { angle: -90 } } : {}),
              },
        ]}
        yAxis={
          hasTwoSeries
            ? [
                { id: 'left', scaleType: 'linear', position: 'left', min: 0, valueFormatter: (v: number) => formatNumberItalian(v) },
                { id: 'right', scaleType: 'linear', position: 'right', min: 0, valueFormatter: (v: number) => formatNumberItalian(v) },
              ]
            : [{ id: 'left', scaleType: 'linear', position: 'left', min: 0, valueFormatter: (v: number) => formatNumberItalian(v) }]
        }
        leftAxis="left"
        rightAxis={hasTwoSeries ? 'right' : null}
        margin={{ top: 40, right: marginRight, left: 90, bottom: bottomMargin }}
      />
    </Box>
  );
}

export function computeCorrelation(
  xValues: number[],
  yValues: (number | null)[]
): CorrelationResult | undefined {
  const yNum = yValues.map(v => (v != null && !Number.isNaN(v) ? v : NaN));
  return linearRegression(xValues, yNum as number[]);
}
